import { useEffect, useState } from "react";
import { Loader2, Database, Download, RefreshCw, ShieldCheck, FileJson, ShieldQuestion, CircleCheck, CircleX } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui-kit/Button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui-kit/Card";
import { fmtBytes, fmtDate, KIND_STYLES } from "@/pages/settings/utils";

function BackupManager() {
  const [data, setData] = useState(null);
  const [running, setRunning] = useState(false);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  const load = () =>
    api.get("/backup/list")
      .then((r) => setData(r.data))
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || e.message));

  useEffect(() => { load(); }, []);

  const flash = (msg) => { setNotice(msg); setTimeout(() => setNotice(""), 3000); };

  const runNow = async () => {
    setRunning(true);
    setError("");
    try {
      await api.post("/backup/run");
      flash("Encrypted off-server backup created.");
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setRunning(false);
    }
  };

  const verifyLatest = async () => {
    setVerifying(true);
    setError("");
    setVerifyResult(null);
    try {
      const r = await api.post("/backup/verify-latest");
      setVerifyResult(r.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setVerifying(false);
    }
  };

  const download = async (url, fallbackName, key) => {    setBusy(key);
    setError("");
    try {
      const r = await api.get(url, { responseType: "blob" });
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const name = m ? m[1] : fallbackName;
      const blobUrl = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      flash(`Downloaded ${name}`);
    } catch (e) {
      setError("Download failed. Please try again.");
    } finally {
      setBusy("");
    }
  };

  if (data === null) {
    return <div className="grid place-items-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="backup-manager">
      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="backup-scheduler-status">
        <span className={cn("inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1",
          data.scheduler_enabled ? "border-status-paid/30 bg-status-paid/10 text-status-paid" : "border-border bg-secondary text-muted-foreground")}>
          <ShieldCheck className="h-3.5 w-3.5" />
          {data.scheduler_enabled ? `Automated daily backup @ ${String(data.schedule_hour_utc).padStart(2, "0")}:00 UTC` : "Automated backups disabled"}
        </span>
        <span className="text-muted-foreground">
          Retention: {data.retention.daily} daily · {data.retention.weekly} weekly · {data.retention.monthly} monthly
        </span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={runNow} disabled={running} data-testid="backup-run-now">
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />}
          {running ? "Backing up…" : "Run backup now"}
        </Button>
        <Button variant="secondary" size="sm" disabled={!!busy}
          onClick={() => download("/backup/export?format=archive", "backup.archive.gz.enc", "exp-archive")}
          data-testid="backup-download-encrypted">
          {busy === "exp-archive" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Download encrypted backup
        </Button>
        <Button variant="secondary" size="sm" disabled={!!busy}
          onClick={() => download("/backup/export?format=json", "export.json", "exp-json")}
          data-testid="backup-download-json">
          {busy === "exp-json" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileJson className="h-4 w-4" />}
          Download JSON export
        </Button>
        <Button variant="secondary" size="sm" onClick={verifyLatest} disabled={verifying || running}
          data-testid="backup-verify-latest">
          {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldQuestion className="h-4 w-4" />}
          {verifying ? "Verifying…" : "Verify latest backup"}
        </Button>
        <Button variant="ghost" size="sm" onClick={load} data-testid="backup-refresh">
          <RefreshCw className="h-4 w-4" /> Refresh
        </Button>
      </div>

      {notice && <p className="text-sm text-status-paid" data-testid="backup-notice">{notice}</p>}
      {error && <p className="text-sm text-destructive" data-testid="backup-error">{error}</p>}

      {verifyResult && (
        <div data-testid="backup-verify-result"
          className={cn("rounded-lg border p-4",
            verifyResult.ok ? "border-status-paid/40 bg-status-paid/5" : "border-destructive/40 bg-destructive/5")}>
          <div className="flex items-center gap-2">
            {verifyResult.ok
              ? <CircleCheck className="h-5 w-5 text-status-paid" data-testid="backup-verify-ok" />
              : <CircleX className="h-5 w-5 text-destructive" data-testid="backup-verify-fail" />}
            <span className="text-sm font-medium text-foreground">
              {verifyResult.ok
                ? `Restore verified — ${verifyResult.total_restored} of ${verifyResult.total_expected} records matched exactly`
                : `Mismatch detected in: ${verifyResult.mismatches.join(", ")}`}
            </span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Backup from {fmtDate(verifyResult.created_at)} ({verifyResult.kind}) was restored into temporary,
            isolated collections and compared record-by-record, then discarded. Your live records were never modified.
          </p>
          <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-3">
            {(verifyResult.collections || []).map((c) => (
              <div key={c.name} data-testid={`backup-verify-row-${c.name}`}
                className="flex items-center justify-between gap-2 text-xs">
                <span className="truncate text-muted-foreground">{c.name}</span>
                <span className={cn("nums", c.match ? "text-foreground" : "font-semibold text-destructive")}>
                  {c.restored}/{c.expected}{c.match ? "" : " ✗"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-lg border border-border" data-testid="backup-list">
        <div className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-3 border-b border-border px-4 py-2 text-xs font-medium text-muted-foreground">
          <span>Type</span><span>Created</span><span className="text-right">Size</span><span></span>
        </div>
        {data.backups.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-muted-foreground" data-testid="backup-empty">
            No backups yet. The first automated backup runs tonight, or create one now.
          </p>
        ) : (
          data.backups.map((b) => (
            <div key={b.id} data-testid={`backup-row-${b.id}`}
              className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-3 border-b border-border px-4 py-2.5 text-sm last:border-b-0">
              <span className={cn("inline-flex w-16 justify-center rounded-md border px-2 py-0.5 text-[11px] font-medium capitalize", KIND_STYLES[b.kind] || KIND_STYLES.manual)}>
                {b.kind}
              </span>
              <span className="text-foreground">
                {fmtDate(b.created_at)}
                {b.status !== "completed" && (
                  <span className="ml-2 text-xs text-destructive">({b.status})</span>
                )}
                {b.total_documents != null && (
                  <span className="ml-2 text-xs text-muted-foreground nums">{b.total_documents} docs</span>
                )}
              </span>
              <span className="text-right text-muted-foreground nums">{fmtBytes(b.size)}</span>
              <button type="button"
                disabled={b.status !== "completed" || busy === b.id}
                onClick={() => download(`/backup/${b.id}/download`, b.filename, b.id)}
                data-testid={`backup-download-${b.id}`}
                className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:bg-secondary hover:text-foreground disabled:opacity-40">
                {busy === b.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                Download
              </button>
            </div>
          ))
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Backups are encrypted (AES) and stored off-server for disaster recovery. Restoring an encrypted
        archive requires the firm&apos;s <code>BACKUP_ENCRYPTION_KEY</code> — keep a copy safe.
      </p>
    </div>
  );
}

export function BackupCard() {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Backups &amp; data export</CardTitle>
        <CardDescription>Automated daily encrypted backups are shipped off-server for disaster recovery. Run an on-demand backup or download a full export at any time.</CardDescription>
      </CardHeader>
      <CardContent><BackupManager /></CardContent>
    </Card>
  );
}
